import type { MessageState } from "../communication/contracts";
import type { AiObjective } from "../ai-messages/contracts";
import type { TaskActionType } from "../../lib/command-contract";

/**
 * Contrato local da definição versionada de funis (Plano 06, Lote 6).
 *
 * Nada aqui executa, inscreve lead, move etapa, cria tarefa, envia mensagem ou
 * abre rede. O domínio descreve a DEFINIÇÃO e a publicação local: rascunhos
 * editáveis, versões imutáveis, referência ativa, plano canônico compilado e a
 * trilha de uma simulação pura. O runner do Plano 07 permanece separado.
 *
 * Os dois conceitos abaixo NÃO se confundem em nenhum ponto do arquivo:
 *   * o cursor do fluxo, que é estado da execução futura;
 *   * a etapa comercial, que pertence à participação e só muda por comando
 *     transacional do domínio de Pipeline.
 * Um nó `stage` produz no máximo uma INTENÇÃO de mudança de etapa.
 */

// ---------------------------------------------------------------------------
// Estrutura do funil e modo de execução
// ---------------------------------------------------------------------------

export const FUNNEL_STRUCTURE_TYPES = ["simple", "graph"] as const;
export type FunnelStructureType = (typeof FUNNEL_STRUCTURE_TYPES)[number];

/** Funil legado, sem decisão persistida, é `simple` e continua Kanban linear. */
export const DEFAULT_FUNNEL_STRUCTURE_TYPE: FunnelStructureType = "simple";

export const funnelStructureTypeLabels: Record<FunnelStructureType, string> = {
  simple: "Funil simples",
  graph: "Fluxograma",
};

export const FLOW_EXECUTION_MODES = ["automatic", "semiautomatic"] as const;
export type FlowExecutionMode = (typeof FLOW_EXECUTION_MODES)[number];

/** Default seguro: nada avança sozinho enquanto não houver decisão persistida. */
export const DEFAULT_FLOW_EXECUTION_MODE: FlowExecutionMode = "semiautomatic";

export const flowExecutionModeLabels: Record<FlowExecutionMode, string> = {
  automatic: "Automático",
  semiautomatic: "Semiautomático",
};

/** Origem do valor efetivo. A interface mostra o valor E de onde ele veio. */
export const FLOW_MODE_SOURCES = ["default", "funnel", "flow", "step"] as const;
export type FlowModeSource = (typeof FLOW_MODE_SOURCES)[number];

export const flowModeSourceLabels: Record<FlowModeSource, string> = {
  default: "Padrão do produto",
  funnel: "Definido no funil",
  flow: "Definido no flow",
  step: "Definido no passo",
};

export interface EffectiveFlowMode {
  mode: FlowExecutionMode;
  source: FlowModeSource;
}

export interface FlowModeOverrides {
  funnelMode?: FlowExecutionMode;
  flowMode?: FlowExecutionMode;
  stepMode?: FlowExecutionMode;
}

/**
 * Hierarquia obrigatória: funil → flow → passo. Função pura, sem estado, sem
 * relógio: o mesmo input devolve sempre o mesmo valor efetivo e sua origem.
 */
export function resolveEffectiveFlowMode(overrides: FlowModeOverrides): EffectiveFlowMode {
  if (overrides.stepMode) return { mode: overrides.stepMode, source: "step" };
  if (overrides.flowMode) return { mode: overrides.flowMode, source: "flow" };
  if (overrides.funnelMode) return { mode: overrides.funnelMode, source: "funnel" };
  return { mode: DEFAULT_FLOW_EXECUTION_MODE, source: "default" };
}

// ---------------------------------------------------------------------------
// Políticas de entrada, reentrada e resposta
// ---------------------------------------------------------------------------

export const FLOW_ENTRY_POLICIES = ["manual", "on_lead_created", "domain_event"] as const;
export type FlowEntryPolicy = (typeof FLOW_ENTRY_POLICIES)[number];

export const flowEntryPolicyLabels: Record<FlowEntryPolicy, string> = {
  manual: "Inscrição manual",
  on_lead_created: "Ao criar o Lead",
  domain_event: "Por evento de domínio permitido",
};

/**
 * Allowlist fechada de gatilhos. A v1 NÃO aceita trigger arbitrário, webhook
 * genérico nem expressão fornecida pelo usuário.
 */
export const FLOW_DOMAIN_EVENT_TRIGGERS = [
  "lead_replied",
  "stage_changed",
  "task_completed",
  "membership_won",
  "membership_lost",
] as const;
export type FlowDomainEventTrigger = (typeof FLOW_DOMAIN_EVENT_TRIGGERS)[number];

export const FLOW_REENTRY_POLICIES = ["ignore", "resume", "start_after_terminal"] as const;
export type FlowReentryPolicy = (typeof FLOW_REENTRY_POLICIES)[number];

/** Já existindo execução ativa futura, o padrão é ignorar a nova inscrição. */
export const DEFAULT_FLOW_REENTRY_POLICY: FlowReentryPolicy = "ignore";

export const flowReentryPolicyLabels: Record<FlowReentryPolicy, string> = {
  ignore: "Ignorar quando já houver execução ativa",
  resume: "Retomar a execução existente",
  start_after_terminal: "Iniciar somente após um desfecho",
};

export const FLOW_REPLY_POLICIES = ["pause", "route_by_category", "continue_and_notify"] as const;
export type FlowReplyPolicy = (typeof FLOW_REPLY_POLICIES)[number];

/**
 * Decisão confirmada de produto para definições legadas sem regra persistida.
 * O comportamento é deliberadamente visível e continua exigindo warning e
 * confirmação no publish futuro; sinais de segurança sempre prevalecem.
 */
export const DEFAULT_FLOW_REPLY_POLICY: FlowReplyPolicy = "continue_and_notify";

export const flowReplyPolicyLabels: Record<FlowReplyPolicy, string> = {
  pause: "Pausar em qualquer resposta",
  route_by_category: "Rotear pela categoria da resposta",
  continue_and_notify: "Continuar e notificar",
};

/**
 * `continue_and_notify` é a opção de maior risco (Plano 09 §1): ela exige aviso
 * no editor e confirmação destacada no publish futuro, que não existe aqui.
 */
export const FLOW_REPLY_POLICY_REQUIRING_CONFIRMATION: FlowReplyPolicy = "continue_and_notify";

// ---------------------------------------------------------------------------
// Nós e portas
// ---------------------------------------------------------------------------

/** Controles estruturais: NÃO contam como passos executáveis nem viram coluna. */
export const FLOW_CONTROL_NODE_TYPES = ["start", "condition", "random_split"] as const;
export type FlowControlNodeType = (typeof FLOW_CONTROL_NODE_TYPES)[number];

export const FLOW_EXECUTABLE_NODE_TYPES = [
  "stage",
  "wait",
  "human_task",
  "send_message",
  "meeting",
  "billing",
  "ai_response",
  "set_field_or_tag",
  "call_flow",
  "won",
  "lost",
  "end",
] as const;
export type FlowExecutableNodeType = (typeof FLOW_EXECUTABLE_NODE_TYPES)[number];

export const FLOW_NODE_TYPES = [
  ...FLOW_CONTROL_NODE_TYPES,
  ...FLOW_EXECUTABLE_NODE_TYPES,
] as const;
export type FlowNodeType = (typeof FLOW_NODE_TYPES)[number];

export const FLOW_TERMINAL_NODE_TYPES: readonly FlowNodeType[] = ["won", "lost", "end"];

export function isFlowControlNode(type: FlowNodeType): type is FlowControlNodeType {
  return (FLOW_CONTROL_NODE_TYPES as readonly string[]).includes(type);
}

export function isFlowTerminalNode(type: FlowNodeType): boolean {
  return FLOW_TERMINAL_NODE_TYPES.includes(type);
}

/**
 * O funil simples é a leitura linear do Kanban atual. Ele aceita espera, tarefa
 * humana, e-mail linear, etapa e desfecho — conforme AGENTS §27.6 — e NÃO
 * aceita condição, A/B, loop, subfluxo, IA nem escrita de campo/tag.
 */
export const SIMPLE_FLOW_ALLOWED_NODE_TYPES: readonly FlowNodeType[] = [
  "start",
  "stage",
  "wait",
  "human_task",
  "send_message",
  "won",
  "lost",
  "end",
];

export const FLOW_EDGE_PORTS = ["out", "match", "fallback", "variant", "return"] as const;
export type FlowEdgePort = (typeof FLOW_EDGE_PORTS)[number];

export const flowEdgePortLabels: Record<FlowEdgePort, string> = {
  out: "Saída única",
  match: "Condição atendida",
  fallback: "Saída padrão",
  variant: "Variante do teste",
  return: "Retorno do subfluxo",
};

/** Portas de saída válidas por tipo de nó. Terminal não possui saída. */
export const flowNodeOutputPorts: Record<FlowNodeType, readonly FlowEdgePort[]> = {
  start: ["out"],
  condition: ["match", "fallback"],
  random_split: ["variant"],
  stage: ["out"],
  wait: ["out"],
  human_task: ["out"],
  send_message: ["out"],
  meeting: ["out"],
  billing: ["out"],
  ai_response: ["out"],
  set_field_or_tag: ["out"],
  call_flow: ["return"],
  won: [],
  lost: [],
  end: [],
};

/** Nós que escolhem entre várias saídas. Todos os demais têm no máximo uma. */
export const FLOW_BRANCHING_NODE_TYPES: readonly FlowNodeType[] = ["condition", "random_split"];

// ---------------------------------------------------------------------------
// Dependências publicadas
// ---------------------------------------------------------------------------

/**
 * Referência a um artefato externo ao flow. Carrega o significado de
 * `PublishedArtifactRef` do Plano Mestre §7 — tipo, ID e versão imutável — sem
 * criar um segundo vocabulário de referência no projeto.
 */
export const FLOW_DEPENDENCY_TYPES = [
  "funnel",
  "stage",
  "lead_field",
  "tag",
  "trigger",
  "loss_reason",
  "message_template_version",
  "ai_prompt_version",
  "reply_classification_version",
  "approach_type_version",
  "external_connection",
  "flow_version",
] as const;
export type FlowDependencyType = (typeof FLOW_DEPENDENCY_TYPES)[number];

export interface FlowDependencyRef {
  type: FlowDependencyType;
  id: string;
  /** Obrigatória para artefato versionado; `latest` nunca é referência válida. */
  version?: number;
  /** Funil de origem da dependência, quando ela pertence a um funil. */
  funnelId?: string;
}

/** Dependências que só podem ser fixadas por versão publicada. */
export const FLOW_VERSIONED_DEPENDENCY_TYPES: readonly FlowDependencyType[] = [
  "message_template_version",
  "ai_prompt_version",
  "reply_classification_version",
  "approach_type_version",
  "flow_version",
];

export function flowDependencyKey(ref: FlowDependencyRef): string {
  return `${ref.type}:${ref.id}:${ref.version ?? ""}`;
}

// ---------------------------------------------------------------------------
// Configuração tipada de cada nó
// ---------------------------------------------------------------------------

export interface FlowFieldAssignment {
  target: "lead_field" | "tag" | "trigger";
  id: string;
  operation: "set" | "clear" | "add" | "remove";
  value?: string | number | boolean | null;
}

/**
 * Quem é convidado para o encontro. O bloco registra a intenção de agendar; a
 * reunião em si pertence à agenda do cadastro.
 */
export const FLOW_MEETING_PARTICIPANTS = ["primary_contact", "all_contacts"] as const;
export type FlowMeetingParticipants = (typeof FLOW_MEETING_PARTICIPANTS)[number];

/** Canais do lembrete de mensalidade, na ordem em que o protótipo os oferece. */
export const FLOW_BILLING_CHANNELS = ["system", "email", "whatsapp"] as const;
export type FlowBillingChannel = (typeof FLOW_BILLING_CHANNELS)[number];

export const FLOW_CALL_SEMANTICS = ["transfer", "call_and_return"] as const;
export type FlowCallSemantics = (typeof FLOW_CALL_SEMANTICS)[number];

export const FLOW_CALL_ORIGIN_POLICIES = ["continue_origin", "end_origin"] as const;
export type FlowCallOriginPolicy = (typeof FLOW_CALL_ORIGIN_POLICIES)[number];

export type FlowCallTarget =
  | {
      scope: "same_funnel";
      flowId: string;
      flowVersionId: string;
      flowVersion: number;
      semantics: FlowCallSemantics;
    }
  | {
      scope: "other_funnel";
      funnelId: string;
      flowId: string;
      flowVersionId: string;
      flowVersion: number;
      originPolicy: FlowCallOriginPolicy;
    };

export type FlowNodeConfig =
  | { type: "start" }
  | { type: "condition" }
  | { type: "random_split"; variants: FlowSplitVariant[] }
  | { type: "stage"; stageId: string }
  | { type: "wait"; durationMinutes: number }
  | {
      type: "human_task";
      instruction: string;
      taskActionType: Extract<TaskActionType, "human_task" | "review" | "sensitive_action">;
      sensitive: boolean;
    }
  | {
      type: "send_message";
      channel: "email";
      templateId: string;
      templateVersionId: string;
      templateVersion: number;
    }
  | { type: "meeting"; participants: FlowMeetingParticipants }
  | { type: "billing"; offsetsDays: number[]; channels: FlowBillingChannel[] }
  | {
      type: "ai_response";
      objective: AiObjective;
      promptVersionId: string;
      promptVersion: number;
    }
  | { type: "set_field_or_tag"; assignments: FlowFieldAssignment[] }
  | { type: "call_flow"; target: FlowCallTarget }
  | { type: "won" }
  | { type: "lost"; lossReasonId: string; stageId: string; evidence: string }
  | { type: "end"; outcomeNote?: string };

export interface FlowSplitVariant {
  key: string;
  label: string;
  /** Peso inteiro; a soma das variantes de um split é exatamente 100. */
  weight: number;
}

export const FLOW_SPLIT_TOTAL_WEIGHT = 100;

/** Posição visual: pertence ao snapshot editorial, nunca ao plano executável. */
export interface FlowNodePosition {
  x: number;
  y: number;
}

export interface FlowNode {
  id: string;
  type: FlowNodeType;
  label: string;
  config: FlowNodeConfig;
  /**
   * Etapa de CRM em que o passo posiciona o cadastro. Qualquer passo pode
   * declará-la; no passo `stage` a configuração é a fonte e este campo a
   * acompanha. Continua sendo intenção: só o comando transacional de
   * participação move etapa e abre visita (§7.2).
   */
  stageId?: string;
  /** Instrução livre do passo, quando o tipo não guarda texto próprio. */
  note?: string;
  /** Override do passo, o nível mais específico da hierarquia de modo. */
  mode?: FlowExecutionMode;
  position?: FlowNodePosition;
}

export interface FlowEdge {
  id: string;
  fromNodeId: string;
  fromPort: FlowEdgePort;
  toNodeId: string;
  /** Ordem determinística de avaliação dentro do mesmo nó/porta. */
  priority: number;
  label?: string;
  /** Somente na porta `match`. Nenhuma expressão livre é aceita. */
  condition?: FlowCondition;
  /** Somente na porta `fallback`, e no máximo uma por nó condicional. */
  fallback: boolean;
  /** Somente na porta `variant`, apontando a variante do split. */
  variantKey?: string;
  /** Obrigatório na aresta que fecha um ciclo. Inteiro de 1 a 100. */
  loopLimit?: number;
}

export const FLOW_LOOP_LIMIT_MIN = 1;
export const FLOW_LOOP_LIMIT_MAX = 100;
export const DEFAULT_FLOW_LOOP_LIMIT = 3;

/** Profundidade máxima de subfluxos encadeados na definição e na simulação. */
export const FLOW_MAX_SUBFLOW_DEPTH = 3;

// ---------------------------------------------------------------------------
// Condições declarativas e tipadas
// ---------------------------------------------------------------------------

export const FLOW_VALUE_TYPES = [
  "text",
  "number",
  "boolean",
  "date",
  "duration",
  "enum",
  "set",
] as const;
export type FlowValueType = (typeof FLOW_VALUE_TYPES)[number];

/** Campos-base fixos do Lead. Todo outro dado usa um campo do formulário. */
export const FLOW_BASE_FIELDS = [
  "name",
  "company",
  "role",
  "segment",
  "source",
  "email",
  "phone",
  "instagram",
  "site",
  "city",
  "state",
  "priority",
  "potentialValue",
] as const;
export type FlowBaseField = (typeof FLOW_BASE_FIELDS)[number];

export const flowBaseFieldValueTypes: Record<FlowBaseField, FlowValueType> = {
  name: "text",
  company: "text",
  role: "text",
  segment: "text",
  source: "text",
  email: "text",
  phone: "text",
  instagram: "text",
  site: "text",
  city: "text",
  state: "text",
  priority: "enum",
  potentialValue: "number",
};

/**
 * Allowlist de eventos de mensagem/entrega. Ela é um subconjunto tipado dos
 * estados já versionados pelo canal (Plano 02) — não um segundo catálogo.
 */
export const FLOW_MESSAGE_EVENTS: readonly MessageState[] = [
  "received",
  "sent",
  "delivered",
  "bounced",
  "complained",
  "rejected",
  "suppressed",
];
export type FlowMessageEvent = MessageState;

export const FLOW_TIME_EVENTS = [
  "lead_created",
  "stage_entered",
  "last_interaction",
  "flow_entered",
  "last_message_sent",
  "last_reply",
] as const;
export type FlowTimeEvent = (typeof FLOW_TIME_EVENTS)[number];

export const FLOW_OPERAND_KINDS = [
  "base_field",
  "custom_field",
  "tag",
  "trigger",
  "stage",
  "membership_status",
  "reply_classification",
  "approach_type",
  "message_event",
  "task_completed",
  "time_since_event",
  "time_in_stage",
  "related_membership",
] as const;
export type FlowOperandKind = (typeof FLOW_OPERAND_KINDS)[number];

export type FlowConditionOperand =
  // Tags e gatilhos são conjuntos: `includes`/`not_includes` recebem o ID do
  // catálogo como literal, e é dali que a dependência publicada é extraída.
  | { kind: "base_field"; field: FlowBaseField }
  | { kind: "custom_field"; fieldId: string; valueType: FlowValueType }
  | { kind: "tag" }
  | { kind: "trigger" }
  | { kind: "stage" }
  | { kind: "membership_status" }
  | { kind: "reply_classification"; catalogVersionId: string; catalogVersion: number }
  | { kind: "approach_type"; catalogVersionId: string; catalogVersion: number }
  | { kind: "message_event"; event: FlowMessageEvent }
  | { kind: "task_completed"; taskNodeId: string }
  | { kind: "time_since_event"; event: FlowTimeEvent }
  | { kind: "time_in_stage" }
  | { kind: "related_membership"; funnelId: string };

export const FLOW_CONDITION_OPERATORS = [
  "equals",
  "not_equals",
  "in",
  "not_in",
  "greater_than",
  "greater_or_equal",
  "less_than",
  "less_or_equal",
  "contains",
  "starts_with",
  "includes",
  "not_includes",
  "is_empty",
  "is_not_empty",
  "exists",
  "not_exists",
] as const;
export type FlowConditionOperator = (typeof FLOW_CONDITION_OPERATORS)[number];

/** Operadores permitidos por tipo. Um operador fora da lista é erro tipado. */
export const flowOperatorsByValueType: Record<FlowValueType, readonly FlowConditionOperator[]> = {
  enum: ["equals", "not_equals", "in", "not_in"],
  boolean: ["equals", "not_equals"],
  number: ["equals", "not_equals", "greater_than", "greater_or_equal", "less_than", "less_or_equal"],
  date: ["equals", "not_equals", "greater_than", "greater_or_equal", "less_than", "less_or_equal"],
  duration: [
    "equals",
    "not_equals",
    "greater_than",
    "greater_or_equal",
    "less_than",
    "less_or_equal",
  ],
  text: ["equals", "not_equals", "contains", "starts_with"],
  set: ["includes", "not_includes", "is_empty", "is_not_empty"],
};

/** Operadores de presença: válidos para qualquer tipo e sem valor literal. */
export const FLOW_PRESENCE_OPERATORS: readonly FlowConditionOperator[] = ["exists", "not_exists"];

/** Operadores que não recebem valor literal. */
export const FLOW_VALUELESS_OPERATORS: readonly FlowConditionOperator[] = [
  "exists",
  "not_exists",
  "is_empty",
  "is_not_empty",
];

/** Operadores de lista: recebem um array de literais. */
export const FLOW_LIST_OPERATORS: readonly FlowConditionOperator[] = ["in", "not_in"];

export type FlowConditionLiteral = string | number | boolean | readonly (string | number)[];

export type FlowCondition =
  | {
      type: "comparison";
      operand: FlowConditionOperand;
      operator: FlowConditionOperator;
      value?: FlowConditionLiteral;
    }
  | { type: "all"; conditions: FlowCondition[] }
  | { type: "any"; conditions: FlowCondition[] }
  | { type: "not"; condition: FlowCondition };

export const FLOW_CONDITION_MAX_DEPTH = 5;

// ---------------------------------------------------------------------------
// Rascunho, versão candidata e versão publicada
// ---------------------------------------------------------------------------

export const FLOW_DRAFT_STATUSES = ["editable", "archived"] as const;
export type FlowDraftStatus = (typeof FLOW_DRAFT_STATUSES)[number];

export interface FlowDraft {
  id: string;
  ownerId: string;
  funnelId: string;
  structureType: FunnelStructureType;
  name: string;
  description: string;
  /** Revisão otimista: `expectedVersion` do comando compara com este número. */
  revision: number;
  /** Override do flow na hierarquia de modo. Opcional por decisão de domínio. */
  mode?: FlowExecutionMode;
  entryPolicy: FlowEntryPolicy;
  entryDomainEvent?: FlowDomainEventTrigger;
  reentryPolicy: FlowReentryPolicy;
  replyPolicy: FlowReplyPolicy;
  /** No máximo um flow de entrada por funil. */
  isEntryFlow: boolean;
  nodes: FlowNode[];
  edges: FlowEdge[];
  status: FlowDraftStatus;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string;
}

/** Alias explícito para o subconjunto linear já validado como `simple`. */
export interface SimpleFlowDefinition extends FlowDraft {
  structureType: "simple";
}

/** Alias explícito para o grafo com um `start` e terminais alcançáveis. */
export interface GraphFlowDefinition extends FlowDraft {
  structureType: "graph";
}

/**
 * Snapshot editorial canônico: preserva o desenho, inclusive posição visual,
 * para diff e reabertura. O plano executável NÃO carrega posição.
 */
export interface FlowCanonicalSnapshot {
  snapshotVersion: number;
  flowId: string;
  ownerId: string;
  funnelId: string;
  structureType: FunnelStructureType;
  name: string;
  description: string;
  mode?: FlowExecutionMode;
  entryPolicy: FlowEntryPolicy;
  entryDomainEvent?: FlowDomainEventTrigger;
  reentryPolicy: FlowReentryPolicy;
  replyPolicy: FlowReplyPolicy;
  isEntryFlow: boolean;
  nodes: FlowNode[];
  edges: FlowEdge[];
}

export const FLOW_SNAPSHOT_VERSION = 1;
export const FLOW_PLAN_VERSION = 1;

/** Nó do plano compilado: neutro de UI e de runner. */
export interface CompiledFlowNode {
  id: string;
  type: FlowNodeType;
  config: FlowNodeConfig;
  /** Etapa declarada no passo; entra no plano porque é semântica de execução. */
  stageId?: string;
  mode: FlowExecutionMode;
  modeSource: FlowModeSource;
  executable: boolean;
  terminal: boolean;
  outgoing: CompiledFlowEdge[];
}

export interface CompiledFlowEdge {
  id: string;
  toNodeId: string;
  port: FlowEdgePort;
  priority: number;
  fallback: boolean;
  variantKey?: string;
  condition?: FlowCondition;
  loopLimit?: number;
  /** Verdadeiro quando a aresta fecha um ciclo detectado na compilação. */
  closesCycle: boolean;
}

export interface CompiledFlowPlan {
  planVersion: number;
  flowId: string;
  ownerId: string;
  funnelId: string;
  structureType: FunnelStructureType;
  mode: FlowExecutionMode;
  modeSource: FlowModeSource;
  entryPolicy: FlowEntryPolicy;
  entryDomainEvent?: FlowDomainEventTrigger;
  reentryPolicy: FlowReentryPolicy;
  replyPolicy: FlowReplyPolicy;
  startNodeId: string;
  nodes: CompiledFlowNode[];
  terminalNodeIds: string[];
  dependencies: FlowDependencyRef[];
  maxSubflowDepth: number;
}

/**
 * Versão candidata: já é imutável e reproduz decisões, mas ainda não recebeu
 * identidade nem instante de publicação.
 */
export interface FlowVersionCandidate {
  ownerId: string;
  flowId: string;
  funnelId: string;
  structureType: FunnelStructureType;
  version: number;
  /** Revisão exata do rascunho que originou este candidato. */
  sourceRevision: number;
  snapshot: FlowCanonicalSnapshot;
  plan: CompiledFlowPlan;
  hash: string;
  dependencies: FlowDependencyRef[];
  changelog: string;
  authorId: string;
  createdAt: string;
}

/** Versão publicada: imutável, nunca editada no lugar. */
export interface FlowVersion extends FlowVersionCandidate {
  id: string;
  publishedAt: string;
}

/**
 * Referência ativa usada apenas por novas inscrições. Publicar outra versão
 * troca esta referência sem alterar runs existentes nem reescrever a versão
 * anterior.
 */
export interface FlowActiveVersion {
  ownerId: string;
  flowId: string;
  funnelId: string;
  flowVersionId: string;
  version: number;
  activatedAt: string;
  /**
   * Instante em que as inscrições foram pausadas. Pausar não desativa a versão
   * publicada nem encerra percursos: é o controle de entrada do funil, separado
   * de pausar execução (§27.9).
   */
  pausedAt?: string;
}

// ---------------------------------------------------------------------------
// Erros e avisos de validação
// ---------------------------------------------------------------------------

export const FLOW_ISSUE_SEVERITIES = ["error", "warning"] as const;
export type FlowIssueSeverity = (typeof FLOW_ISSUE_SEVERITIES)[number];

/** Códigos estáveis: mensagem pode evoluir, código não. */
export const FLOW_VALIDATION_CODES = [
  "flow.start.missing",
  "flow.start.multiple",
  "flow.terminal.missing",
  "flow.terminal.unreachable",
  "flow.node.unreachable",
  "flow.node.duplicated_id",
  "flow.node.invalid_config",
  "flow.edge.duplicated_id",
  "flow.edge.dangling",
  "flow.edge.port_incompatible",
  "flow.edge.ambiguous_priority",
  "flow.edge.self_reference",
  "flow.condition.no_valid_output",
  "flow.condition.multiple_fallback",
  "flow.condition.missing_expression",
  "flow.condition.invalid_operator",
  "flow.condition.invalid_value",
  "flow.split.invalid_weights",
  "flow.split.unmapped_variant",
  "flow.parallelism.implicit",
  "flow.dependency.not_found",
  "flow.dependency.cross_owner",
  "flow.dependency.wrong_funnel",
  "flow.dependency.not_published",
  "flow.dependency.version_not_pinned",
  "flow.lost.missing_requirements",
  "flow.simple.forbidden_node",
  "flow.simple.forbidden_branch",
  "flow.cycle.without_wait",
  "flow.cycle.without_exit",
  "flow.cycle.without_limit",
  "flow.cycle.limit_out_of_range",
  "flow.subflow.recursive",
  "flow.subflow.depth_exceeded",
  "flow.subflow.transfer_with_return",
  "flow.reply_policy.missing",
  "flow.entry.invalid_trigger",
  "flow.owner.mismatch",
  "flow.structure.mismatch",
] as const;
export type FlowValidationErrorCode = (typeof FLOW_VALIDATION_CODES)[number];

export const FLOW_WARNING_CODES = [
  "flow.warning.reply_continue_and_notify",
  "flow.warning.dependency_archived",
  "flow.warning.unlikely_path",
  "flow.warning.terminal_without_objective",
  "flow.warning.external_action_disabled",
] as const;
export type FlowValidationWarningCode = (typeof FLOW_WARNING_CODES)[number];

export type FlowValidationCode = FlowValidationErrorCode | FlowValidationWarningCode;

export interface FlowValidationIssue {
  code: FlowValidationCode;
  severity: FlowIssueSeverity;
  /** Mensagem humana em português, exibida no painel de validação. */
  message: string;
  nodeId?: string;
  edgeId?: string;
  dependency?: FlowDependencyRef;
  recommendedAction: string;
}

export interface FlowValidationResult {
  ok: boolean;
  issues: FlowValidationIssue[];
  errors: FlowValidationIssue[];
  warnings: FlowValidationIssue[];
}

// ---------------------------------------------------------------------------
// Simulação: intenções, trilha e erros
// ---------------------------------------------------------------------------

export const FLOW_INTENT_KINDS = [
  "would_wait",
  "would_create_human_task",
  "would_queue_message",
  "would_schedule_meeting",
  "would_remind_billing",
  "would_request_ai",
  "would_set_field_or_tag",
  "would_change_stage",
  "would_call_flow",
  "would_resolve_related_lead",
  "would_mark_won",
  "would_mark_lost",
  "would_end",
] as const;
export type FlowIntentKind = (typeof FLOW_INTENT_KINDS)[number];

/**
 * Intenções são descrições do que ACONTECERIA. Nenhuma delas chama comando,
 * store, provider ou persistência: a simulação é totalmente inerte.
 */
export type FlowSimulationIntent =
  | { kind: "would_wait"; nodeId: string; durationMinutes: number }
  | {
      kind: "would_create_human_task";
      nodeId: string;
      instruction: string;
      taskActionType: string;
      sensitive: boolean;
    }
  | {
      kind: "would_queue_message";
      nodeId: string;
      channel: "email";
      templateId: string;
      templateVersionId: string;
      templateVersion: number;
    }
  | { kind: "would_schedule_meeting"; nodeId: string; participants: FlowMeetingParticipants }
  | {
      kind: "would_remind_billing";
      nodeId: string;
      offsetsDays: number[];
      channels: FlowBillingChannel[];
    }
  | {
      kind: "would_request_ai";
      nodeId: string;
      objective: AiObjective;
      promptVersionId: string;
      promptVersion: number;
    }
  | { kind: "would_set_field_or_tag"; nodeId: string; assignments: FlowFieldAssignment[] }
  | { kind: "would_change_stage"; nodeId: string; stageId: string; alreadyCurrentStage: boolean }
  | {
      kind: "would_call_flow";
      nodeId: string;
      scope: "same_funnel" | "other_funnel";
      flowId: string;
      flowVersionId: string;
      flowVersion: number;
      semantics?: FlowCallSemantics;
      originPolicy?: FlowCallOriginPolicy;
    }
  | {
      kind: "would_resolve_related_lead";
      nodeId: string;
      companyId: string;
      targetFunnelId: string;
      flowVersionId: string;
      flowVersion: number;
    }
  | { kind: "would_mark_won"; nodeId: string }
  | {
      kind: "would_mark_lost";
      nodeId: string;
      lossReasonId: string;
      stageId: string;
      evidence: string;
    }
  | { kind: "would_end"; nodeId: string; outcomeNote?: string };

export interface FlowEvaluatedInput {
  operandKey: string;
  operandKind: FlowOperandKind;
  valueType: FlowValueType;
  present: boolean;
  value?: FlowConditionLiteral | null;
  /** Verdadeiro quando o valor foi redigido por política de privacidade. */
  redacted: boolean;
}

export type FlowConditionOutcome = true | false | "unknown";

export interface FlowConditionEvaluation {
  expression: FlowCondition;
  inputs: FlowEvaluatedInput[];
  result: FlowConditionOutcome;
  reason: string;
}

export interface FlowSimulationStep {
  index: number;
  /** Identifica de forma inequívoca o plano que produziu este passo. */
  flowId: string;
  /** Versão candidata/publicada usada neste nível da chamada. */
  flowVersion: number;
  nodeId: string;
  nodeType: FlowNodeType;
  mode: FlowExecutionMode;
  modeSource: FlowModeSource;
  /** Quantas vezes este nó já foi visitado nesta simulação, a partir de 1. */
  iteration: number;
  depth: number;
  chosenEdgeId?: string;
  chosenPort?: FlowEdgePort;
  variantKey?: string;
  conditionEvaluations: FlowConditionEvaluation[];
  intent?: FlowSimulationIntent;
}

export const FLOW_SIMULATION_ERROR_CODES = [
  "invalid_input",
  "context_mismatch",
  "company_missing",
  "max_steps_exceeded",
  "loop_limit_reached",
  "subflow_depth_exceeded",
  "subflow_plan_missing",
  "subflow_context_missing",
  "subflow_plan_mismatch",
  "dead_end",
  "start_missing",
] as const;
export type FlowSimulationErrorCode = (typeof FLOW_SIMULATION_ERROR_CODES)[number];

export interface FlowSimulationError {
  code: FlowSimulationErrorCode;
  message: string;
  nodeId?: string;
  edgeId?: string;
}

export interface FlowSimulationTrace {
  ok: boolean;
  flowId: string;
  /** Versão usada como parte da alocação determinística de A/B. */
  flowVersion: number;
  planHash: string;
  identity: string;
  steps: FlowSimulationStep[];
  intents: FlowSimulationIntent[];
  terminalNodeId?: string;
  error?: FlowSimulationError;
}

export const DEFAULT_FLOW_SIMULATION_MAX_STEPS = 200;

// ---------------------------------------------------------------------------
// Capabilities: publicação e execução nascem desligadas
// ---------------------------------------------------------------------------

/** Definição de flows existe apenas como candidato local; nada foi ativado. */
export const FLOW_DEFINITIONS_ENABLED = false;
/** A capability remota de publicação continua desligada. */
export const FLOW_PUBLICATION_ENABLED = false;
/** Runner, inscrição e execução pertencem ao Plano 07 e continuam desligados. */
export const FLOW_EXECUTION_ENABLED = false;
